/**
 * Jobs this labeler was just bounced out of because they had no task to give
 * them. The jobs list still shows them (lebron lists every in-flight job, not
 * just ones this user can claim), so the table marks them for a while instead.
 * Session-scoped and best-effort: storage can be unavailable, in which case
 * nothing is marked.
 */

const STORAGE_KEY = "oos:no-task-jobs";
/** How long a bounced job stays marked. */
export const NO_TASK_MARK_TTL_MS = 5 * 60 * 1000;

type Marks = Record<string, number>;

const read = (): Marks => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const write = (marks: Marks) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(marks));
  } catch {
    // Storage unavailable — marks just don't persist.
  }
};

/** The ids still within their TTL, pruning expired ones from storage. */
export const readNoTaskJobIds = (now = Date.now()): Set<string> => {
  const marks = read();
  const live: Marks = {};
  Object.entries(marks).forEach(([id, at]) => {
    if (typeof at === "number" && now - at < NO_TASK_MARK_TTL_MS) live[id] = at;
  });
  if (Object.keys(live).length !== Object.keys(marks).length) write(live);
  return new Set(Object.keys(live));
};

export const markNoTaskJob = (jobId: string, now = Date.now()) => {
  write({ ...read(), [jobId]: now });
};
